import React, {useState, ChangeEvent, FormEvent} from 'react';
import {Link, useLocation} from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import logo from '../../assets/dashboard/logo.png';
import {useRegister} from '../../hooks/useApi';
import {AuthService} from '../../services/authService';
import {Countries, States, Ethnicities} from '@/services/constants';
import {RegistrationRequest} from '../../types/api';

interface FormState {
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    birthday: Date | null;
    gender: string;
    ethnicity: string;
    address1: string;
    address2: string;
    city: string;
    state: string;
    zip: string;
    country: string;
    license: string;
    ref: string;
}

interface FormErrors {
    [key: string]: boolean;
}

const RegisterPage: React.FC = () => {
    const ENABLE_ID_STEP = false;
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const ref = query.get('ref');

    const [formState, setFormState] = useState<FormState>({
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        birthday: null,
        gender: '',
        ethnicity: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        zip: '',
        country: 'United States',
        license: '',
        ref: ref || '',
    });

    const [step, setStep] = useState(0);
    const [sent, setSent] = useState(false);
    const [errors, setErrors] = useState<FormErrors>({});
    const [errorMsg, setErrorMsg] = useState('');
    const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

    const registerMutation = useRegister();

    const getLoginLink = () => (ref ? `/login?ref=${ref}` : '/login');
    const stepTitles = ENABLE_ID_STEP
        ? ['Personal Info', 'Demographics', 'Address', 'ID (Optional)', 'Confirm']
        : ['Personal Info', 'Demographics', 'Address', 'Confirm'];
    const finalStep = stepTitles.length - 1;

    const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const name = event.target.name;
        let value =
            event.target.type === 'checkbox'
                ? (event.target as HTMLInputElement).checked.toString()
                : event.target.value;

        // Phone formatting: XXX-XXX-XXXX
        if (name === 'phone') {
            const digitReg = /^[0-9]$/;
            if (value.length === 4 || value.length === 8) {
                if (value[value.length - 1] !== '-') {
                    value = value.substring(0, value.length - 1) + '-' + value[value.length - 1];
                } else {
                    value = value.substring(0, value.length - 1);
                }
            }
            if (value !== '' && (!digitReg.test(value[value.length - 1]) || value.length > 12)) {
                return;
            }
        }

        // ZIP code: 5 digits only
        if (name === 'zip') {
            const digitReg = /^[0-9]$/;
            if (value !== '' && (!digitReg.test(value[value.length - 1]) || value.length > 5)) {
                return;
            }
        }

        setFormState((prev) => ({...prev, [name]: value}));
        setErrors((prev) => ({...prev, [name]: false}));
    };

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        setErrors({});
        setErrorMsg('');

        const phoneReg = /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/;
        const emailReg =
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

        const newErrors: FormErrors = {
            firstName: step === 0 && formState.firstName === '',
            lastName: step === 0 && formState.lastName === '',
            phone: step === 0 && !phoneReg.test(formState.phone),
            email: step === 0 && !emailReg.test(formState.email),
            birthday: step === 1 && !formState.birthday,
            gender: step === 1 && formState.gender === '',
            ethnicity: step === 1 && formState.ethnicity === '',
            address1: step === 2 && formState.address1 === '',
            city: step === 2 && formState.city === '',
            state: step === 2 && formState.state === '',
            zip: step === 2 && formState.zip === '',
            country: step === 2 && formState.country === '',
        };

        setErrors(newErrors);

        const hasErrors = Object.values(newErrors).some((error) => error);
        if (hasErrors) {
            setErrorMsg('Please fill out all required fields');
            return;
        }

        if (step < finalStep) {
            setStep(step + 1);
            return;
        }

        // Final step - submit registration
        try {
            const registrationData: RegistrationRequest = {
                firstName: formState.firstName,
                lastName: formState.lastName,
                email: formState.email,
                phone: formState.phone,
                birthday: formState.birthday ? formState.birthday.toISOString() : '',
                gender: formState.gender,
                ethnicity: formState.ethnicity,
                address1: formState.address1,
                address2: formState.address2 || undefined,
                city: formState.city,
                state: formState.state,
                zip: formState.zip,
                country: formState.country,
                license: formState.license || undefined,
                ref: formState.ref || undefined,
            };

            await registerMutation.mutateAsync(registrationData);
            setSent(true);
        } catch (error: unknown) {
            const apiError = error as {status?: number; message?: string};
            if (apiError.status === 409) {
                setErrors({accountExists: true});
                setErrorMsg('An account with that email already exists. Please reset your password.');
            } else {
                setErrorMsg(apiError.message || 'Registration failed. Please try again.');
            }
        }
    };

    const handleResend = async () => {
        setResendStatus('sending');
        try {
            const registrationData: RegistrationRequest = {
                firstName: formState.firstName,
                lastName: formState.lastName,
                email: formState.email,
                phone: formState.phone,
                birthday: formState.birthday ? formState.birthday.toISOString() : '',
                gender: formState.gender,
                ethnicity: formState.ethnicity,
                address1: formState.address1,
                address2: formState.address2 || undefined,
                city: formState.city,
                state: formState.state,
                zip: formState.zip,
                country: formState.country,
                license: formState.license || undefined,
                ref: formState.ref || undefined,
            };
            await AuthService.resendVerificationEmail(registrationData);
            setResendStatus('sent');
        } catch {
            setResendStatus('error');
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-mld-background py-8 px-4">
            <div className="w-full max-w-md">
                {/* Logo */}
                <div className="text-center mb-8">
                    <Link to={getLoginLink()}>
                        <img src={logo} alt="MyLabsDirect" className="mx-auto h-6" />
                    </Link>
                </div>

                {/* Card Container */}
                <div className="bg-white rounded-lg shadow-lg p-8">
                    {!sent ? (
                        <>
                            {/* Header with Back Button */}
                            <div className="flex items-center mb-6">
                                {step > 0 && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setErrors({});
                                            setErrorMsg('');
                                            setStep(step - 1);
                                        }}
                                        className="mr-3 p-1 text-gray-500 hover:text-gray-700 transition-colors">
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M15 19l-7-7 7-7"
                                            />
                                        </svg>
                                    </button>
                                )}
                                <div>
                                    <h2 className="text-2xl font-light text-gray-800">Create your account</h2>
                                    <p className="text-sm text-gray-500">
                                        Step {step + 1} of {stepTitles.length}: {stepTitles[step]}
                                    </p>
                                </div>
                            </div>

                            {/* Progress Bar */}
                            <div className="flex gap-2 mb-6">
                                {stepTitles.map((_title, i) => (
                                    <div
                                        key={i}
                                        className={`flex-1 h-1 rounded-full ${
                                            i <= step ? 'bg-mld-login-button' : 'bg-gray-200'
                                        }`}
                                    />
                                ))}
                            </div>

                            {/* Error Message */}
                            {errorMsg && (
                                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                                    <p className="text-red-600 text-sm">{errorMsg}</p>
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-4">
                                {/* Step 0: Personal Info */}
                                {step === 0 && (
                                    <>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    First Name
                                                </label>
                                                <input
                                                    type="text"
                                                    name="firstName"
                                                    value={formState.firstName}
                                                    onChange={handleChange}
                                                    placeholder="John"
                                                    className={`w-full px-4 py-3 border ${
                                                        errors.firstName ? 'border-red-500' : 'border-gray-300'
                                                    } rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-mld-primary outline-none transition-colors`}
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Last Name
                                                </label>
                                                <input
                                                    type="text"
                                                    name="lastName"
                                                    value={formState.lastName}
                                                    onChange={handleChange}
                                                    placeholder="Doe"
                                                    className={`w-full px-4 py-3 border ${
                                                        errors.lastName ? 'border-red-500' : 'border-gray-300'
                                                    } rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-mld-primary outline-none transition-colors`}
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Phone
                                            </label>
                                            <input
                                                type="tel"
                                                name="phone"
                                                value={formState.phone}
                                                onChange={handleChange}
                                                placeholder="555-555-5555"
                                                className={`w-full px-4 py-3 border ${
                                                    errors.phone ? 'border-red-500' : 'border-gray-300'
                                                } rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-mld-primary outline-none transition-colors`}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Email
                                            </label>
                                            <input
                                                type="email"
                                                name="email"
                                                value={formState.email}
                                                onChange={handleChange}
                                                placeholder="example@example.com"
                                                className={`w-full px-4 py-3 border ${
                                                    errors.email ? 'border-red-500' : 'border-gray-300'
                                                } rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-mld-primary outline-none transition-colors`}
                                            />
                                        </div>
                                    </>
                                )}

                                {/* Step 1: Demographics */}
                                {step === 1 && (
                                    <>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Date of Birth
                                            </label>
                                            <DatePicker
                                                selected={formState.birthday}
                                                onChange={(date) => {
                                                    setFormState((prev) => ({...prev, birthday: date}));
                                                    setErrors((prev) => ({...prev, birthday: false}));
                                                }}
                                                minDate={new Date('1/1/1870')}
                                                maxDate={new Date()}
                                                placeholderText="MM/DD/YYYY"
                                                showMonthDropdown
                                                showYearDropdown
                                                scrollableYearDropdown
                                                dropdownMode="select"
                                                className={`w-full px-4 py-3 border ${
                                                    errors.birthday ? 'border-red-500' : 'border-gray-300'
                                                } rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-mld-primary outline-none transition-colors`}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Biological Sex
                                            </label>
                                            <div className="flex gap-4">
                                                <label
                                                    className={`flex-1 flex items-center justify-center px-4 py-3 border ${
                                                        formState.gender === 'Male'
                                                            ? 'border-mld-login-button bg-mld-login-button/10'
                                                            : errors.gender
                                                              ? 'border-red-500'
                                                              : 'border-gray-300'
                                                    } rounded-lg cursor-pointer hover:border-mld-login-button transition-colors`}>
                                                    <input
                                                        type="radio"
                                                        name="gender"
                                                        value="Male"
                                                        checked={formState.gender === 'Male'}
                                                        onChange={handleChange}
                                                        className="sr-only"
                                                    />
                                                    <span
                                                        className={
                                                            formState.gender === 'Male'
                                                                ? 'text-mld-login-button font-medium'
                                                                : 'text-gray-700'
                                                        }>
                                                        Male
                                                    </span>
                                                </label>
                                                <label
                                                    className={`flex-1 flex items-center justify-center px-4 py-3 border ${
                                                        formState.gender === 'Female'
                                                            ? 'border-mld-login-button bg-mld-login-button/10'
                                                            : errors.gender
                                                              ? 'border-red-500'
                                                              : 'border-gray-300'
                                                    } rounded-lg cursor-pointer hover:border-mld-login-button transition-colors`}>
                                                    <input
                                                        type="radio"
                                                        name="gender"
                                                        value="Female"
                                                        checked={formState.gender === 'Female'}
                                                        onChange={handleChange}
                                                        className="sr-only"
                                                    />
                                                    <span
                                                        className={
                                                            formState.gender === 'Female'
                                                                ? 'text-mld-login-button font-medium'
                                                                : 'text-gray-700'
                                                        }>
                                                        Female
                                                    </span>
                                                </label>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Ethnicity
                                            </label>
                                            <select
                                                name="ethnicity"
                                                value={formState.ethnicity}
                                                onChange={handleChange}
                                                className={`w-full px-4 py-3 border ${
                                                    errors.ethnicity ? 'border-red-500' : 'border-gray-300'
                                                } rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-mld-primary outline-none transition-colors bg-white`}>
                                                <option disabled value="">
                                                    Select ethnicity
                                                </option>
                                                {Ethnicities.map((ethnicity) => (
                                                    <option key={ethnicity} value={ethnicity}>
                                                        {ethnicity}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <p className="text-xs text-gray-500 flex items-center gap-1">
                                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                <path
                                                    fillRule="evenodd"
                                                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
                                                    clipRule="evenodd"
                                                />
                                            </svg>
                                            This data helps us better deliver accurate results
                                        </p>
                                    </>
                                )}

                                {/* Step 2: Address */}
                                {step === 2 && (
                                    <>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Street Address
                                            </label>
                                            <input
                                                type="text"
                                                name="address1"
                                                value={formState.address1}
                                                onChange={handleChange}
                                                placeholder="123 Apple Street"
                                                className={`w-full px-4 py-3 border ${
                                                    errors.address1 ? 'border-red-500' : 'border-gray-300'
                                                } rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-mld-primary outline-none transition-colors`}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Address Cont. - Apt/Suite/Etc{' '}
                                                <span className="text-gray-400 font-normal">(Optional)</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="address2"
                                                value={formState.address2}
                                                onChange={handleChange}
                                                placeholder="Apt 100"
                                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-mld-primary outline-none transition-colors"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                                            <input
                                                type="text"
                                                name="city"
                                                value={formState.city}
                                                onChange={handleChange}
                                                placeholder="Dallas"
                                                className={`w-full px-4 py-3 border ${
                                                    errors.city ? 'border-red-500' : 'border-gray-300'
                                                } rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-mld-primary outline-none transition-colors`}
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    State
                                                </label>
                                                <select
                                                    name="state"
                                                    value={formState.state}
                                                    onChange={handleChange}
                                                    className={`w-full px-4 py-3 border ${
                                                        errors.state ? 'border-red-500' : 'border-gray-300'
                                                    } rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-mld-primary outline-none transition-colors bg-white text-sm`}>
                                                    <option disabled value="">
                                                        --
                                                    </option>
                                                    {States.map((usstate) => (
                                                        <option key={usstate.code} value={usstate.code}>
                                                            {usstate.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    ZIP Code
                                                </label>
                                                <input
                                                    type="text"
                                                    name="zip"
                                                    value={formState.zip}
                                                    onChange={handleChange}
                                                    placeholder="10001"
                                                    className={`w-full px-4 py-3 border ${
                                                        errors.zip ? 'border-red-500' : 'border-gray-300'
                                                    } rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-mld-primary outline-none transition-colors`}
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Country
                                            </label>
                                            <select
                                                name="country"
                                                value={formState.country}
                                                onChange={handleChange}
                                                className={`w-full px-4 py-3 border ${
                                                    errors.country ? 'border-red-500' : 'border-gray-300'
                                                } rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-mld-primary outline-none transition-colors bg-white`}>
                                                <option disabled value="">
                                                    -- Select Country --
                                                </option>
                                                {Countries.map((country) => (
                                                    <option key={country} value={country}>
                                                        {country}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </>
                                )}

                                {ENABLE_ID_STEP && step === 3 && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            {`Passport/Driver's License Number`}
                                        </label>
                                        <input
                                            type="text"
                                            name="license"
                                            value={formState.license}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-mld-primary outline-none transition-colors"
                                        />
                                        <p className="mt-1 text-xs text-gray-500">Optional, required for travel</p>
                                    </div>
                                )}

                                {/* Terms of Service confirmation */}
                                {step === finalStep && (
                                    <p className="text-sm text-gray-600">
                                        {`By continuing to sign up, you're agreeing to our `}
                                        <a
                                            href="https://mylabsdirect.com/terms-of-service/"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-mld-login-button hover:underline">
                                            Terms of Service
                                        </a>
                                        {' and '}
                                        <a
                                            href="https://mylabsdirect.com/privacy-policy/"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-mld-login-button hover:underline">
                                            Privacy Policy
                                        </a>
                                        .
                                    </p>
                                )}

                                <button
                                    type="submit"
                                    disabled={registerMutation.isPending}
                                    className="w-full bg-mld-login-button hover:bg-opacity-90 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed mt-6">
                                    {registerMutation.isPending
                                        ? 'Creating Account...'
                                        : step === finalStep
                                          ? 'Sign Up'
                                          : 'Continue'}
                                </button>

                                <div className="text-center mt-4">
                                    <Link
                                        to={getLoginLink()}
                                        className="text-mld-muted-text hover:text-mld-login-button text-sm transition-colors">
                                        Already have an account? Sign in
                                    </Link>
                                </div>
                            </form>
                        </>
                    ) : (
                        /* Success - Email Sent */
                        <div className="text-center">
                            <div className="mb-6">
                                <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-cyan-400 to-pink-500 rounded-full">
                                    <svg
                                        className="w-10 h-10 text-white"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                        />
                                    </svg>
                                </div>
                            </div>
                            <p className="text-gray-600 mb-2">{formState.email}</p>
                            <h3 className="text-xl font-semibold text-gray-800 mb-4">Check Your Email</h3>
                            <p className="text-gray-600 mb-6">
                                Please check your email and click on the provided link to activate your account. Make
                                sure to check your Spam folder.
                            </p>
                            <button
                                onClick={handleResend}
                                disabled={resendStatus === 'sending'}
                                className="text-mld-login-button hover:underline disabled:opacity-50 disabled:cursor-not-allowed">
                                {resendStatus === 'idle' && 'Resend verification email'}
                                {resendStatus === 'sending' && 'Sending...'}
                                {resendStatus === 'sent' && 'Email successfully resent!'}
                                {resendStatus === 'error' && 'Could not resend email. Try again?'}
                            </button>
                            <div className="mt-6">
                                <Link
                                    to={getLoginLink()}
                                    className="text-mld-muted-text hover:text-mld-login-button transition-colors">
                                    Back to sign in
                                </Link>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RegisterPage;
